import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { DateInput } from '../../../ui/DateInput/DateInput';
import {
  IClubMemberDto,
  IFamilyMemberDto,
  IMembershipTypeLookupDto,
  ISystemCodeByTypeDto
} from '../../../../utils/constants/interfaces';
import { useMembershipDetails } from './useMembershipDetails';
import styles from './MembershipDetailsSection.module.css';

type ClubMemberKey = keyof IClubMemberDto;
type FamilyMemberKey = keyof IFamilyMemberDto;

interface MembershipDetailsSectionProps {
  clubMember: IClubMemberDto;
  fieldErrors: string[];
  membershipTypes: IMembershipTypeLookupDto[];
  loadingMembershipTypes: boolean;
  interests: ISystemCodeByTypeDto[];
  loadingInterests: boolean;
  selectedInterests: number[];
  isSaving: boolean;
  disabled?: boolean;
  onUpdateClubMember: <K extends ClubMemberKey>(key: K, value: IClubMemberDto[K]) => void;
  onUpdateMember: <K extends FamilyMemberKey>(key: K, value: IFamilyMemberDto[K]) => void;
  onInterestChange: (interestId: number, checked: boolean) => void;
}

export const MembershipDetailsSection = ({
  clubMember,
  fieldErrors,
  membershipTypes,
  loadingMembershipTypes,
  interests,
  loadingInterests,
  selectedInterests,
  isSaving,
  disabled = false,
  onUpdateClubMember,
  onUpdateMember,
  onInterestChange
}: MembershipDetailsSectionProps) => {
  const manager = useMembershipDetails({
    clubMember,
    interests,
    selectedInterests
  });

  return (
    <div className={styles.card}>
      <h4 className={styles.sectionTitle}>Membership & Preferences</h4>

      {/* Membership Details */}
      <Row className="g-2 mb-2">
        <Col md={4}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label
              className={
                fieldErrors.includes('overview:membershipTypeId')
                  ? `${styles.required} is-invalid`
                  : styles.required
              }>
              Member Type
            </Form.Label>
            <Form.Select
              size="sm"
              disabled={disabled}
              value={clubMember.membershipTypeId || ''}
              onChange={(e) => onUpdateClubMember('membershipTypeId', Number(e.target.value))}
              isInvalid={fieldErrors.includes('overview:membershipTypeId')}>
              <option value="">- Select -</option>
              {membershipTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
            {loadingMembershipTypes && (
              <small className="text-muted">
                <Spinner size="sm" animation="border" className="me-1" /> Loading...
              </small>
            )}
          </Form.Group>
        </Col>
        <Col md={2}>
          <DateInput
            label="First Joined"
            disabled={disabled}
            value={clubMember.primaryMember.firstJoinedDateUtc}
            onChange={(date) => onUpdateMember('firstJoinedDateUtc', date)}
            isInvalid={fieldErrors.includes('overview:firstJoinedDateUtc')}
            required
          />
        </Col>
        <Col md={2}>
          <DateInput
            label="Joined"
            disabled={disabled}
            value={clubMember.primaryMember.joinedDateUtc}
            onChange={(date) => onUpdateMember('joinedDateUtc', date)}
            isInvalid={fieldErrors.includes('overview:joinedDateUtc')}
            required
          />
        </Col>
        <Col md={2}>
          <Form.Group
            className={`${styles.compactFormGroup} d-flex align-items-center justify-content-center h-100`}>
            <Form.Check
              type="checkbox"
              id="nraMember"
              label="NRA Member"
              disabled={disabled}
              checked={clubMember.primaryMember.isNraMember}
              onChange={(e) => onUpdateMember('isNraMember', e.target.checked)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>NRA Number</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              disabled={disabled}
              value={clubMember.primaryMember.nraNumber}
              onChange={(e) => onUpdateMember('nraNumber', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Membership Date Fields */}
      <Row className="g-2 mb-2">
        <Col md={3}>
          <DateInput
            label="Regular Member Date"
            disabled={disabled}
            value={clubMember.primaryMember.regularMemberDateUtc}
            onChange={(date) => onUpdateMember('regularMemberDateUtc', date)}
          />
        </Col>
        <Col md={3}>
          <DateInput
            label="Honorary Member Date"
            disabled={disabled}
            value={clubMember.primaryMember.honoraryMemberDateUtc}
            onChange={(date) => onUpdateMember('honoraryMemberDateUtc', date)}
          />
        </Col>
        <Col md={3}>
          <DateInput
            label="Senior Member Date"
            disabled={disabled}
            value={clubMember.primaryMember.seniorMemberDateUtc}
            onChange={(date) => onUpdateMember('seniorMemberDateUtc', date)}
          />
        </Col>
        <Col md={3}>
          <DateInput
            label="Life Member Date"
            disabled={disabled}
            value={clubMember.primaryMember.lifeMemberDateUtc}
            onChange={(date) => onUpdateMember('lifeMemberDateUtc', date)}
          />
        </Col>
      </Row>

      {/* Notes */}
      <Row className="g-2 mb-3">
        <Col md={12}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              disabled={disabled}
              value={clubMember.notes}
              onChange={(e) => onUpdateClubMember('notes', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Interests Section */}
      <h5 className={styles.subSectionTitle}>Interests & Activities</h5>
      <Row className="g-2 mb-2">
        <Col md={12}>
          <Form.Group className={styles.compactFormGroup}>
            <Form.Label>Interests</Form.Label>
            {loadingInterests ? (
              <div className="d-flex align-items-center mt-2">
                <Spinner size="sm" animation="border" className="me-2" />
                <span className="text-muted">Loading interests...</span>
              </div>
            ) : (
              <div className={styles.interestsContainer}>
                {interests.length === 0 ? (
                  <p className="text-muted">No interests available</p>
                ) : (
                  <div className="row">
                    {interests.map((interest) => (
                      <div key={interest.id} className="col-md-3 mb-2">
                        <Form.Check
                          type="checkbox"
                          id={`interest-${interest.id}`}
                          label={interest.codeValue}
                          checked={selectedInterests.includes(interest.id)}
                          onChange={(e) => onInterestChange(interest.id, e.target.checked)}
                          disabled={isSaving || disabled}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};
